import { useEffect, useState } from "react";
// import { useCookies } from "react-cookie";
import { useGA4React } from "ga-4-react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useTenant from "contexts/Tenant/useTenant";
// import getDomain from "utils/getDomain";

/** Used when a route has no title of its own, and for the root path. */
const BRAND_TITLE = "HEGIAS VR";

/** The brand suffix the translated titles carry: " | HEGIAS VR", " | HEGIAS RV", " | HEGIAS". */
const BRAND_SUFFIX = /\s*\|\s*HEGIAS(\s+(VR|RV))?\s*$/;

/**
 * Swaps the HEGIAS brand for the organization's `meta.tabName`, so its
 * subdomain reads "Projects | <tabName>" and plain "<tabName>" where the route
 * has no title of its own. Without a tabName the title is left as it is.
 */
const brandTitle = (title: string, tabName?: string): string => {
	if (!tabName) {
		return title;
	}
	if (title === BRAND_TITLE) {
		return tabName;
	}
	return `${title.replace(BRAND_SUFFIX, "")} | ${tabName}`;
};

const PageTitles = () => {
	const ga = useGA4React(process.env.REACT_APP_GOOGLE_ANALYTICS);

	const { t } = useTranslation();

	const tabName = useTenant().tenant?.meta?.tabName?.trim();

	const [title, setTitle] = useState(document.title);
	// const [cookies, setCookie] = useCookies(["existSidebar"]);

	const location = useLocation();

	const { pathname } = location;

	/**
	 * Title for a path, falling back to progressively shorter key paths.
	 *
	 * i18next returns the key itself when nothing matches, so an unmapped route
	 * used to put a literal `page.meta.error.404.title` in the browser tab.
	 * Walking up means /error/404 lands on the generic error title even if the
	 * specific one is ever missing, and an entirely unknown route still gets a
	 * real name rather than a broken one.
	 */
	const resolveTitle = (segments: string[]): string => {
		for (let i = segments.length; i > 0; i -= 1) {
			const key = `page.meta.${segments.slice(0, i).join(".")}.title`;
			const value = String(t(key));

			if (value !== key) {
				return value;
			}
		}

		return BRAND_TITLE;
	};

	useEffect(() => {
		document.title = brandTitle(title, tabName);
	}, [title, tabName]);

	useEffect(() => {
		if (ga && !process.env.REACT_APP_DEBUG) {
			if (pathname.includes("projects/")) {
				if (pathname.includes("scenes/")) {
					ga.pageview("projects/:id/scenes/:id");
				} else {
					ga.pageview("projects/:id/scenes");
				}
			} else if (pathname.includes("library/")) {
				const libraryTokens = pathname.split("/");
				libraryTokens.pop();
				libraryTokens.push(":id");

				const libraryPath = libraryTokens.join("/");
				ga.pageview(libraryPath);
			} else if (pathname.includes("/password")) {
				ga.pageview("password");
			} else if (pathname.includes("/verify")) {
				ga.pageview("verify");
			} else if (pathname.includes("/invite")) {
				ga.pageview("invite");
			} else {
				ga.pageview(pathname);
			}
		}

		let tokens = pathname.split("/").filter((token) => token);
		if (pathname.includes("scenes/")) {
			tokens = ["scene"];
		} else if (pathname.includes("projects/")) {
			tokens = ["project"];
		} else if (pathname.includes("library")) {
			tokens = ["library"];
		} else if (pathname.includes("/password")) {
			tokens = ["password"];
		} else if (pathname.includes("/verify")) {
			tokens = ["verify"];
		} else if (pathname.includes("/invite")) {
			tokens = ["invite"];
		}

		setTitle(resolveTitle(tokens));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, ga]);

	return null;
};

export default PageTitles;
